import { useState, createContext, useContext, Dispatch } from 'react';
import { Outlet } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import Alert, { AlertProps } from '@mui/material/Alert';
import Box from '@mui/material/Box';
// import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
// import IconButton from '@mui/material/IconButton';
// import CloseIcon from '@mui/icons-material/Close';
import { DaylitLogo, Flow } from '@lendica/components';
import { DaylitTheme } from '@lendica/theme';
// import { xDomainAuthUrl } from '../api';

interface LoginValue {
    email?: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
    otpCode?: string;
    verifyToken?: string;
}

type ContainerType = {
    msg: string | null;
    setMsg: Dispatch<string | null>;
    error: string | null;
    setError: Dispatch<string | null>;
    successMsg: string | null;
    setSuccessMsg: Dispatch<string | null>;
    loginValue: LoginValue;
    updateLoginValue: (updates: Partial<LoginValue>) => void;
    resetLoginValue: () => void;
    pageLoading: boolean;
    setPageLoading: Dispatch<boolean>;
    // iframeRef: MutableRefObject<HTMLIFrameElement | null>;
    // channel: MessageChannel | null;
};

const AppContainerContext = createContext<ContainerType | undefined>(undefined);

export const useAppContainerContext = () => {
    let context = useContext(AppContainerContext);
    if (context === undefined) {
        throw Error('AppContainerContext undefined');
    }
    return context;
};

export const AppContainer = () => {
    const [msg, setMsg] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [loginValue, setLoginValue] = useState<LoginValue>({});
    const [pageLoading, setPageLoading] = useState(false);

    const updateLoginValue = (updates: Partial<LoginValue>) => {
        setLoginValue({ ...loginValue, ...updates });
    };

    const resetLoginValue = () => {
        setLoginValue({});
    };

    // const iframeRef = useRef<HTMLIFrameElement>(null);
    // const channel = new MessageChannel();

    // const handleClose = (
    //     event: Event | React.SyntheticEvent<any, Event>,
    //     reason?: SnackbarCloseReason
    // ) => {
    //     if (reason === 'clickaway') {
    //         return;
    //     }
    //     setMsg(null);
    // };

    return (
        <AppContainerContext.Provider
            value={{
                msg,
                setMsg,
                error,
                setError,
                successMsg,
                setSuccessMsg,
                // iframeRef,
                // channel,
                loginValue,
                updateLoginValue,
                resetLoginValue,
                pageLoading,
                setPageLoading,
            }}
        >
            {/* <iframe
                ref={iframeRef}
                id="lendica-receiver"
                name="lendica-receiver"
                src={xDomainAuthUrl}
                tabIndex={-1}
                aria-hidden="true"
                style={{
                    width: '1px',
                    height: '1px',
                    position: 'absolute',
                    top: '-100px',
                    display: 'none',
                    visibility: 'hidden',
                }}
            ></iframe> */}
            <Box
                display="flex"
                justifyContent="center"
                flex={1}
                sx={{
                    minHeight: '100vh',
                    boxSizing: 'border-box',
                    backgroundColor: 'background.paper',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        py: 3,
                        width: '100%',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'inherit',
                        zIndex: 1000,
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            boxSizing: 'border-box',
                            px: '20px',
                        }}
                    >
                        <Box
                            sx={{
                                'display': 'flex',
                                'alignItems': 'flex-start',
                                'justifyContent': 'flex-start',
                                'width': 400,
                                'margin': '0 20px',
                                ['@media (max-width:400px)']: {
                                    width: '100%',
                                },
                                '& svg': {
                                    display: 'block',
                                },
                            }}
                        >
                            <DaylitLogo height={18.5} color={DaylitTheme.Palette.Burgundy} />
                        </Box>
                        <Collapse in={!!successMsg}>
                            <AlertMessage severity="success" onClose={() => setSuccessMsg(null)}>
                                {successMsg}
                            </AlertMessage>
                        </Collapse>
                        <Collapse in={!!error}>
                            <AlertMessage severity="error" onClose={() => setError(null)}>
                                {error}
                            </AlertMessage>
                        </Collapse>
                        <Collapse in={!!msg}>
                            <AlertMessage severity="info" onClose={() => setMsg(null)}>
                                {msg}
                            </AlertMessage>
                        </Collapse>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                        width: 400,
                        margin: '0 20px',
                        position: 'relative',
                        scrollBehavior: 'smooth',
                        height: 'inherit',
                        ['@media (max-width:400px)']: {
                            width: '100%',
                        },
                    }}
                >
                    {pageLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <Flow />
                        </Box>
                    ) : (
                        <Outlet />
                    )}
                    {/* <Snackbar
                        open={!!msg}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        autoHideDuration={6000}
                        onClose={handleClose}
                        message={msg}
                        action={
                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={handleClose}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                    /> */}
                </Box>
            </Box>
        </AppContainerContext.Provider>
    );
};

const AlertMessage = ({ ...props }: AlertProps) => {
    return (
        <Alert
            {...props}
            sx={{
                my: 3,
                width: 400,
                boxSizing: 'border-box',
                ['@media (max-width:400px)']: {
                    width: '100%',
                },
                ...props.sx,
            }}
        >
            {props.children}
        </Alert>
    );
};
